import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, TicketCheck, Clock, CheckCircle, X, Send, AlertTriangle } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

const PRIORITIES = ["Low","Medium","High"];
const CATEGORIES = ["Technical Issue","Payroll","Leave","Attendance","HR Policy","Account Access","Other"];

export default function SupportTickets() {
  const { T } = useTheme();
  const P_COLOR = { High:T.red, Medium:T.amber, Low:T.green };
  const S_COLOR = { Open:T.amber,"In Progress":T.cyan, Resolved:T.green, Closed:T.muted };

  const inp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  const user      = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const companyId = user.company_id;

  const [tickets, setTickets] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState("");
  const [form,    setForm]    = useState({ title:"", description:"", priority:"Medium", category:"Technical Issue" });

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const load = async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/tickets/?company_id=${companyId}`, ax());
      setTickets(r.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId]);

  const submit = async () => {
    if (!form.title) return;
    try {
      await axios.post(`${API}/tickets/`, {
        ...form,
        company_id: companyId,
        created_by: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
      }, ax());
      setOpen(false);
      setForm({ title:"", description:"", priority:"Medium", category:"Technical Issue" });
      load(); flash("✅ Ticket submitted — our team will respond shortly");
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
  };

  const stats = {
    open:       tickets.filter(t=>t.status==="Open").length,
    inProgress: tickets.filter(t=>t.status==="In Progress").length,
    resolved:   tickets.filter(t=>t.status==="Resolved").length,
  };

  return (
    <div style={{ fontFamily:"Outfit,sans-serif", color:T.text }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>SUPPORT</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Support Tickets</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Raise and track issues with the platform</p>
        </div>
        <button onClick={()=>setOpen(true)}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"10px 20px", borderRadius:9, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
          <Plus size={14}/> Raise Ticket
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"Open",        val:stats.open,       color:T.amber },
          { label:"In Progress", val:stats.inProgress, color:T.cyan },
          { label:"Resolved",    val:stats.resolved,   color:T.green },
        ].map(({label,val,color})=>(
          <div key={label} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:16, borderTop:`3px solid ${color}` }}>
            <div style={{ fontSize:26, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
            <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tickets list */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:T.muted }}>Loading…</div>
        ) : tickets.length === 0 ? (
          <div style={{ padding:60, textAlign:"center", color:T.muted }}>
            <TicketCheck size={40} style={{ color:T.dim, marginBottom:12 }}/>
            <div style={{ fontSize:15, fontWeight:600, color:T.text, marginBottom:6 }}>No tickets yet</div>
            <div style={{ fontSize:13 }}>Click "Raise Ticket" if you have an issue</div>
          </div>
        ) : tickets.map((t,i) => (
          <div key={t.id} style={{ padding:"16px 20px", borderBottom:i<tickets.length-1?`1px solid ${T.toggle_bg}`:"none" }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                  <span style={{ fontSize:11, fontFamily:"JetBrains Mono,monospace", color:T.dim }}>{t.ticket_no||`#${t.id}`}</span>
                  <span style={{ fontSize:14, fontWeight:600 }}>{t.title}</span>
                  <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${P_COLOR[t.priority]||T.amber}26`, color:P_COLOR[t.priority]||T.amber }}>{t.priority}</span>
                  <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${T.purple}1A`, color:T.purple }}>{t.category}</span>
                </div>
                {t.description && <div style={{ fontSize:12.5, color:T.muted, marginBottom:6 }}>{t.description}</div>}
                {t.resolution_notes && (
                  <div style={{ padding:"8px 12px", background:`${T.green}0D`, border:`1px solid ${T.green}1A`, borderRadius:8, fontSize:12.5, color:T.green, marginTop:6 }}>
                    <strong>Resolution:</strong> {t.resolution_notes}
                  </div>
                )}
                <div style={{ fontSize:11.5, color:T.dim, marginTop:6 }}>
                  {t.created_at ? new Date(t.created_at).toLocaleDateString("en-IN") : "—"}
                </div>
              </div>
              <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, flexShrink:0,
                background:`${S_COLOR[t.status]||T.muted}26`, color:S_COLOR[t.status]||T.muted }}>
                {t.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* New ticket modal */}
      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:480, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Raise Support Ticket</h3>
              <button onClick={()=>setOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:13 }}>
              <div><label style={lbl}>Title *</label><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Brief summary of the issue" style={inp(!form.title)}/></div>
              <div><label style={lbl}>Description</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} placeholder="Detailed description of the issue…" style={{...inp(),resize:"vertical"}}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Category</label>
                  <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={inp()}>
                    {CATEGORIES.map(c=><option key={c} style={{ background:T.opt_bg }}>{c}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Priority</label>
                  <select value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})} style={inp()}>
                    {PRIORITIES.map(p=><option key={p} style={{ background:T.opt_bg }}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ background:`${T.cyan}0A`, border:`1px solid ${T.border}`, borderRadius:8, padding:"9px 14px", fontSize:12.5, color:T.muted }}>
                ℹ️ Our support team will review and respond to your ticket shortly.
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setOpen(false)} style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={submit} style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 20px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                  <Send size={13}/> Submit Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
